/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILabelProps } from '@fluentui/react';
import { BaseButton, Button, IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Target } from '@fluentui/react/lib/Callout';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluBrush } from 'src/clu/appShell/cluTagUtterancesPage/components/CluBrushContainer';
import { CluGptPredictedUtteranceStore } from 'src/clu/core/stores/CluGptPredictedUtteranceStore';
import { CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { LabelDto } from 'src/clu/modules/hooks/useCluUtteranceActions';
import { transformUtteranceLabels } from 'src/clu/modules/utils/labelingUtils';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { tokenToCharIndices, transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';
import { inBetween } from 'src/luis/modules/labeling/utils/generalUtils';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { Label } from 'src/modules/labeling/types/Label';
import { Selection } from 'src/modules/labeling/types/Selection';
import { INVALID_INDEX } from 'src/modules/labeling/utils/labelingReducer';
import { ITokenStore, Labeler, LabelerProps, LabelerRef } from 'src/packages/react-text-annotator/lib/esm';

export type CluUtteranceRendererProps = {
    /**
     * The labeling store that encapsulate all the UI states.
     */
    labelingStore: LabelingStore;

    /**
     * The entity selected from the entity palette.
     */
    sidePaneSelectedEntity: string;

    /**
     * Utterance store.
     */
    utterance: CluUtteranceStore;

    /**
     * Callback to accept generated utterances by gpt.
     */
    acceptGeneratedUtterance: (utteranceId: number) => void;

    /**
     * Callback to reject generated utterances by gpt.
     */
    rejectGeneratedUtterance: (utteranceId: number) => void;

    /**
     * Callback to add labels to utterances.
     */
    addLabel: (utteranceId: number, label: LabelDto, method: 'menu' | 'brush' | 'createNewEntity') => void;

    /**
     * Whether labeling is disabled in the case of
     * an orchestration clu project.
     */
    isLabelingDisabled: boolean;

    /**
     * Array of all entities.
     */
    entities: EntityStore[];

    /**
     * Callback to edit utterances.
     */
    onEditUtterance: (utteranceId: number) => void;

    /**
     * Callback to delete utterances.
     */
    onDeleteUtterance: () => void;

    /**
     * Wether the user can edit the utterance or not.
     */
    canWrite: boolean;

    /**
     *  The displayed user role.
     */
    displayedUserRole: string;

    /**
     * The id of the selected Entity in brush mode.
     */
    selectedEntityId: string;
};

const messages = defineMessages({
    edit: { id: 'clu.cluUtteranceRenderer.edit', defaultMessage: 'Edit utterance' },
    delete: { id: 'clu.cluUtteranceRenderer.delete', defaultMessage: 'Delete utterance' },
    generatedByGpt: { id: 'clu.cluUtteranceRenderer.generatedByGpt', defaultMessage: 'AI suggested utterance' }
});

const rendererTheme = getScopedTheme('CluUtteranceRenderer');
const { styleComponent } = getStylistV2('CluUtteranceRenderer');

const StyledStack = styleComponent<IStackProps>(Stack)('StyledStack', () => ({ width: '100%', overflowX: 'auto' }));
const StyledLabeler = styleComponent<LabelerProps<ITokenStore> & ILabelProps>(Labeler)('StyledLabeler', theme => ({
    margin: theme.labelerMargin
}));
const OptionsContainer = styleComponent<IStackProps>(Stack)('OptionsContainer', { marginTop: '10px', backgroundColor: 'transparent' });
const acceptUtteranceBtnStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { color: rendererTheme.acceptButtonColor, padding: 0, height: 'auto', width: 'auto' },
    icon: { margin: 0 }
});
const rejectUtteranceBtnStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { color: rendererTheme.rejectButtonColor, padding: 0, height: 'auto', width: 'auto' },
    icon: { margin: 0 }
});

const getSelectedText = (text: string, tokenizedText: string[], startTokenIndex: number, endTokenIndex: number) => {
    const charIndices = tokenToCharIndices({
        text,
        tokenizedText,
        endTokenIndex,
        startTokenIndex,
        isCharacterTokenization: true
    });

    return text.substring(charIndices.startCharIndex, charIndices.endCharIndex + 1);
};

// A generated class attached to the entity dropdown menu to be used for keyboard accessibility.
export const entityDropDownFocusId = getId();

/**
 * The view of the first cell in the utterances table that contain:
 * 1. The utterance text itself or the editable input field if the edit mode is enabled.
 * 2. The contextual menu of the specified utterance to support additional actions to be applied to the selected utterance.
 */
export const CluUtteranceRenderer = observer((props: CluUtteranceRendererProps) => {
    const {
        canWrite,
        entities,
        addLabel,
        utterance,
        labelingStore,
        onEditUtterance,
        selectedEntityId,
        displayedUserRole,
        onDeleteUtterance,
        isLabelingDisabled,
        sidePaneSelectedEntity,
        rejectGeneratedUtterance,
        acceptGeneratedUtterance
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { labels } = transformUtteranceLabels(utterance, entities);

    const [isHovering, setIsHovering] = React.useState(false);

    const labelerRef = React.useRef<LabelerRef>();
    const labelerKey = React.useRef<string>(getId());
    const utteranceRef = React.useRef<HTMLDivElement>();

    const { brushStore, menuStore } = labelingStore;

    const resolveShapeColor = React.useCallback((label: Label) => entities.find(e => e.id === label.entity.id)?.color, [entities]);

    const { id: projectId } = rootStore.currentCluProjectStore;
    const isRowDisabled = labelingStore.disabledUtteranceId === utterance.id;
    const annotations = labels.map(label => transformLabelToAnnotation(label, null, resolveShapeColor));
    const annotationToLabelMap = annotations.reduce((acc, a, index) => acc.set(a.id, labels[index]), new Map<string, Label>());

    const labelerConfig: LabelerProps<ITokenStore>['labelerConfigs'] = React.useMemo(
        () => ({
            isRtl: isDocumentSwaggerRtl(utterance.language as Language['code']),
            isDisabled: isRowDisabled || isLabelingDisabled || !canWrite || utterance.dataset === 'Generated',
            tokenizationType: isDocumentSwaggerWordTokenized(utterance.language as Language['code']) ? 'word' : 'character'
        }),
        [isRowDisabled, isLabelingDisabled, utterance.language]
    );

    React.useEffect(() => {
        const tableRowGroup = document.querySelectorAll('[role="rowgroup"]')[0] as HTMLElement;
        const tableRows = tableRowGroup?.querySelectorAll('[role="row"]');

        tableRows?.forEach(element =>
            (element as HTMLElement).addEventListener('keydown', e => {
                const target = e.target as HTMLElement;
                const labelerElement = target.querySelector('[data-is-line="true"]') as HTMLElement;
                if (e.shiftKey) {
                    return;
                }
                if (e.key === LuisKeyCodes.Escape) {
                    target.blur();
                    return;
                }
                if (e.key === LuisKeyCodes.Tab) {
                    e.stopPropagation();
                    e.preventDefault();
                    labelerElement?.focus();
                }

                labelerElement?.addEventListener('keydown', event => {
                    if (event.shiftKey) {
                        return;
                    }
                    if (event.key === LuisKeyCodes.Escape) {
                        target.blur();
                        return;
                    }
                    if (event.key === LuisKeyCodes.Tab) {
                        event.stopPropagation();
                        event.preventDefault();
                        labelerElement?.blur();
                    }
                });
            })
        );
    }, []);

    const handleSelect = React.useCallback(
        (selection?: Selection, target?: Target) => {
            if (!selection || selection[0] === INVALID_INDEX) {
                return;
            }

            transaction(() => {
                const selectedText = getSelectedText(utterance.text, utterance.tokenizedText, selection[0], selection[1]);
                labelingStore.setTokenSelectionMetadata({ selection: selection as Selection, utteranceId: utterance.id, selectedText });

                if (labelingStore.mode === 'menu') {
                    if (utterance.id !== menuStore.currentUtteranceId) {
                        menuStore.setCurrentUtteranceId(utterance.id);
                    }
                    menuStore.setTarget(target);

                    return;
                }

                if (sidePaneSelectedEntity) {
                    addLabel(
                        utterance.id,
                        { startTokenIndex: selection[0], endTokenIndex: selection[1], entityId: sidePaneSelectedEntity },
                        'brush'
                    );
                }
                menuStore.resetMenuState();
                labelerRef.current.selectionStore.cancelSelection();
            });
        },
        [labelingStore, labelerRef, menuStore, labels, utterance, brushStore, sidePaneSelectedEntity]
    );

    const handleShapeClick = React.useCallback(
        (label: Label, target: Target) => {
            transaction(() => {
                if (utterance.id !== menuStore.currentUtteranceId) {
                    menuStore.setCurrentUtteranceId(utterance.id);
                }

                labelingStore.setTokenSelectionMetadata({
                    utteranceId: utterance.id,
                    selection: [label.start, label.end],
                    selectedText: getSelectedText(utterance.text, utterance.tokenizedText, label.start, label.end)
                });
                menuStore.setTarget(target);
                menuStore.setClickedOnLabel(label);
            });
        },
        [labelingStore, menuStore, labels]
    );

    const handleTokenHover = React.useCallback(
        (tokenIndex: number) => {
            transaction(() => {
                if (labelingStore.mode !== 'brush') {
                    return;
                }

                if (brushStore.currentUtteranceId !== utterance.id) {
                    brushStore.setCurrentUtteranceId(utterance.id);
                }

                const hoveredEntities = labels
                    .filter(({ start, end, renderedAs }) => inBetween(start, end)(tokenIndex) && renderedAs === 'line')
                    .map(({ entity }) => entity);

                if (!hoveredEntities.length && !brushStore.hoveredEntitiesIds.length) {
                    return;
                }

                brushStore.setHoveredEntitiesIds(hoveredEntities.map(e => e.id as string));
            });
        },
        [brushStore, labels]
    );

    const handleTokenHoverExit = React.useCallback(() => {
        transaction(() => {
            brushStore.setCurrentUtteranceId(null);

            if (brushStore.hoveredEntitiesIds.length) {
                brushStore.setHoveredEntitiesIds([]);
            }
        });
    }, [brushStore]);

    const getEditUtteranceButton = (disabled: boolean) => (
        <IconButton
            disabled={disabled}
            iconProps={{ iconName: 'Edit' }}
            ariaLabel={localize(messages.edit)}
            data-automation-id="editUtteranceButton"
            onClick={() => onEditUtterance(utterance.id)}
        />
    );

    const getDeleteUtteranceButton = (disabled: boolean) => (
        <IconButton
            disabled={disabled}
            onClick={onDeleteUtterance}
            iconProps={{ iconName: 'Delete' }}
            ariaLabel={localize(messages.delete)}
            data-automation-id="deleteUtteranceButton"
        />
    );

    const onWheelPredicate = (e: Event) => {
        const menuContainers = document.querySelectorAll('.ms-ContextualMenu-container');

        if ([...menuContainers].some(container => container.contains(e.target as Node))) {
            return true;
        }

        return false;
    };

    const onAcceptGeneratedUtterance = (
        e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | Button | HTMLSpanElement, MouseEvent>
    ) => {
        e.stopPropagation();
        logButtonClick(getPortalFromUrl().serviceName, 'DataLabeling', 'AcceptGeneratedUtterance', null, projectId);
        acceptGeneratedUtterance(utterance.id);
    };

    const onRejectGeneratedUtterance = (
        e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | Button | HTMLSpanElement, MouseEvent>
    ) => {
        e.stopPropagation();
        logButtonClick(getPortalFromUrl().serviceName, 'DataLabeling', 'RejectGeneratedUtterance', null, projectId);
        rejectGeneratedUtterance(utterance.id);
    };

    return (
        <StyledStack grow verticalFill>
            <StyledStack
                grow
                horizontal
                verticalFill
                verticalAlign={utterance.dataset === 'Generated' ? 'center' : 'start'}
                onFocus={() => setIsHovering(true)}
                onBlur={() => setIsHovering(false)}
                onMouseLeave={() => setIsHovering(false)}
                onMouseEnter={() => setIsHovering(true)}
                horizontalAlign={labelerConfig.isRtl ? 'end' : utterance.dataset === 'Generated' ? 'start' : 'space-between'}>
                <div data-is-focusable="true" ref={utteranceRef}>
                    <Stack.Item styles={{ root: { width: '100%', overflow: 'hidden' } }}>
                        {utterance.dataset === 'Generated' ? (
                            <LuisText styles={{ root: { paddingLeft: 10, paddingBottom: 5 } }}>
                                {(utterance as CluGptPredictedUtteranceStore).updatedText ?? utterance.text}
                            </LuisText>
                        ) : (
                            <StyledLabeler
                                labelerHeight="unset"
                                text={utterance.text}
                                labelerOverflow="unset"
                                data-selection-disabled
                                originalRef={labelerRef}
                                maxCharactersPerLine={70}
                                annotations={annotations}
                                labelerConfigs={labelerConfig}
                                key={`${labelerKey.current} ${annotations.length}`}
                                globalEventExceptionSelectors={{ onWheel: onWheelPredicate }}
                                onSelectionChange={(s, e, targets) => handleSelect([s, e], targets.start)}
                                onAnnotationClick={(a, event) => handleShapeClick(annotationToLabelMap.get(a.id), event.target as Element)}
                                tokenEventListenersWithDeps={{
                                    factory: ({ tokenStore }) => ({
                                        onMouseLeave: handleTokenHoverExit,
                                        onMouseEnter: _ => handleTokenHover(tokenStore.index)
                                    }),
                                    deps: []
                                }}
                            />
                        )}
                    </Stack.Item>
                </div>
                {utterance.dataset === 'Generated' && (
                    <Stack verticalFill grow tokens={{ childrenGap: 4, padding: '0 0 0 20px' }} horizontal verticalAlign="center">
                        <IconButton
                            styles={acceptUtteranceBtnStyles}
                            onClick={onAcceptGeneratedUtterance}
                            iconProps={{ iconName: 'Completed' }}
                        />

                        <IconButton
                            styles={rejectUtteranceBtnStyles}
                            onClick={onRejectGeneratedUtterance}
                            iconProps={{ iconName: 'ErrorBadge' }}
                        />

                        <Stack grow horizontalAlign="end">
                            {canWrite ? (
                                <LuisTooltip content={localize(messages.edit)}>{getEditUtteranceButton(false)}</LuisTooltip>
                            ) : (
                                <RBACTooltip
                                    hidden={false}
                                    displayedUserRole={displayedUserRole}
                                    data-automation-id="editUtteranceButtonLanguageToolTip"
                                    componentSurroundedByToolTip={getEditUtteranceButton(true)}
                                />
                            )}
                        </Stack>
                    </Stack>
                )}
                {isHovering && utterance.dataset !== 'Generated' && (
                    <OptionsContainer horizontal verticalAlign="start">
                        {canWrite ? (
                            <LuisTooltip content={localize(messages.edit)}>{getEditUtteranceButton(false)}</LuisTooltip>
                        ) : (
                            <RBACTooltip
                                hidden={false}
                                displayedUserRole={displayedUserRole}
                                data-automation-id="editUtteranceButtonLanguageToolTip"
                                componentSurroundedByToolTip={getEditUtteranceButton(true)}
                            />
                        )}

                        {canWrite ? (
                            <LuisTooltip content={localize(messages.delete)}>{getDeleteUtteranceButton(false)}</LuisTooltip>
                        ) : (
                            <RBACTooltip
                                hidden={false}
                                displayedUserRole={displayedUserRole}
                                data-automation-id="deleteUtteranceButtonLanguageToolTip"
                                componentSurroundedByToolTip={getDeleteUtteranceButton(true)}
                            />
                        )}
                    </OptionsContainer>
                )}

                <CluBrush
                    mode={labelingStore.mode}
                    attachedToRef={utteranceRef}
                    entity={entities.find(entity => entity.id === selectedEntityId)}
                />
            </StyledStack>
            {utterance.dataset === 'Generated' && (
                <LuisText variant="xSmall" styles={{ root: { paddingLeft: 10 } }} color="#6F37AE">
                    {localize(messages.generatedByGpt)}
                </LuisText>
            )}
        </StyledStack>
    );
});
